/**
 * Shared date-format constants + active date-fns locale (DESIGN.md §17: dates
 * go through a single formatter, never inline `'dd/MM/yyyy'` literals, and are
 * locale-aware). The active locale follows the i18n language (en-AU default,
 * zh-CN when the UI is Chinese) so date-fns `format` / `formatDistanceToNow`
 * render month names + relative phrases in the right language.
 */
import { format, isValid, parseISO } from 'date-fns';
import { enAU, zhCN, type Locale } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

/** Absolute date, e.g. "5 Jul 2026". */
export const DATE_FORMAT = 'd MMM yyyy';
/**
 * Day and short month with no year, e.g. "5 Jul": a day inside a period whose
 * label already carries the year (`formatPeriodEnds`), or a chart tick.
 */
export const DAY_MONTH_FORMAT = 'd MMM';
/** Absolute date + time, e.g. "5 Jul 2026, 14:30". */
export const DATE_TIME_FORMAT = 'd MMM yyyy, HH:mm';
/**
 * Timestamp for CSV exports, e.g. "2026-07-05 14:30". Deliberately NOT locale
 * aware: a spreadsheet has to sort it as text and an operator in another region
 * has to read it unambiguously, which "5/7/26" fails on both counts.
 */
export const CSV_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';
/** Date stamp for generated filenames, e.g. "2026-07-05" (sorts in a folder). */
export const FILE_DATE_FORMAT = 'yyyy-MM-dd';
/**
 * Value of an `<input type="date">`, e.g. "2026-07-05". The HTML spec fixes
 * this shape regardless of the browser's display locale, so it is never
 * locale aware.
 */
export const DATE_INPUT_FORMAT = 'yyyy-MM-dd';

/**
 * Chinese reads a date year, month, day with its own characters
 * ("2026年10月11日", date-fns zhCN `PPP`). `DATE_FORMAT` under zhCN gives
 * "11 10月 2026": English day order with 月 dropped in.
 */
const ZH_DATE_FORMAT = "yyyy'年'M'月'd'日'";
const ZH_DATE_TIME_FORMAT = "yyyy'年'M'月'd'日' HH:mm";
const ZH_DAY_MONTH_FORMAT = "M'月'd'日'";

const isChinese = (locale: Locale): boolean => locale.code.startsWith('zh');

/** Shown in place of an empty or unparseable date. */
const EMPTY_DATE = '--';

/**
 * Parse a stored date value. A bare `YYYY-MM-DD` (a DATE column such as a PO's
 * eta_date) parses as LOCAL midnight of that day, so it shows on its own day in
 * every timezone (`new Date('2026-07-01')` would be UTC midnight, a day early
 * west of UTC). Anything else is an ISO instant. Null when empty or invalid.
 *
 * PR #1989 review: moved here from the PO dates panel so any surface showing a
 * stored date uses one parser.
 */
export function parseDateValue(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
}

/**
 * Date-only display: "19 Jun 2026", or "2026年6月19日" in Chinese; `--` when
 * empty or unparseable.
 */
export function formatDay(value: string | null | undefined, locale: Locale): string {
  const date = parseDateValue(value);
  if (!date) return EMPTY_DATE;
  return format(date, isChinese(locale) ? ZH_DATE_FORMAT : DATE_FORMAT, { locale });
}

/**
 * Day and month only: "5 Jul", or "7月5日" in Chinese; `--` when empty or
 * unparseable. A bare `YYYY-MM-DD` is LOCAL midnight (`parseDateValue`), so no
 * browser zone moves the day.
 */
export function formatDayMonth(value: string | null | undefined, locale: Locale): string {
  const date = parseDateValue(value);
  if (!date) return EMPTY_DATE;
  return format(date, isChinese(locale) ? ZH_DAY_MONTH_FORMAT : DAY_MONTH_FORMAT, { locale });
}

/**
 * The two ends of a period label, for "{{from}} to {{to}}": the year once, on
 * the end ("1 Sep" to "15 Oct 2026"), or on both ends when the years differ
 * ("15 Dec 2025" to "15 Jan 2026"). Chinese: "9月1日" to "2026年10月15日".
 */
export function formatPeriodEnds(from: string, to: string, locale: Locale): { from: string; to: string } {
  const sameYear = parseDateValue(from)?.getFullYear() === parseDateValue(to)?.getFullYear();
  return {
    from: sameYear ? formatDayMonth(from, locale) : formatDay(from, locale),
    to: formatDay(to, locale),
  };
}

/**
 * Date + time display: "19 Jun 2026, 14:30", or "2026年6月19日 14:30" in
 * Chinese; `--` when empty or unparseable.
 */
export function formatDateTime(value: string | null | undefined, locale: Locale): string {
  const date = parseDateValue(value);
  if (!date) return EMPTY_DATE;
  return format(date, isChinese(locale) ? ZH_DATE_TIME_FORMAT : DATE_TIME_FORMAT, { locale });
}

/** Map an i18n language code to its date-fns locale. */
export function dateFnsLocaleFor(lng: string | undefined): Locale {
  return lng?.startsWith('zh') ? zhCN : enAU;
}

/** The active date-fns locale, following the current i18n language. */
export function useDateFnsLocale(): Locale {
  const { i18n } = useTranslation();
  return dateFnsLocaleFor(i18n.language);
}
